export default function Changelog() {
  const releases = [
    {
      version: '1.2.0',
      date: '2024-01-15',
      type: 'minor',
      changes: [
        { type: 'feature', text: 'Added support for custom policy functions' },
        { type: 'feature', text: 'New bundle optimization flags' },
        { type: 'improvement', text: 'Improved policy evaluation performance by 40%' },
        { type: 'fix', text: 'Fixed race condition in concurrent evaluations' },
      ],
    },
    {
      version: '1.1.2',
      date: '2024-01-08',
      type: 'patch',
      changes: [
        { type: 'fix', text: 'Fixed memory leak in long-running processes' },
        { type: 'fix', text: 'Corrected timezone handling in audit logs' },
        { type: 'docs', text: 'Updated SDK documentation with new examples' },
      ],
    },
    {
      version: '1.1.1',
      date: '2024-01-02',
      type: 'patch',
      changes: [
        { type: 'fix', text: 'Fixed CLI authentication issue on Windows' },
        { type: 'improvement', text: 'Better error messages for invalid policies' },
      ],
    },
    {
      version: '1.1.0',
      date: '2023-12-20',
      type: 'minor',
      changes: [
        { type: 'feature', text: 'Introduced policy versioning system' },
        { type: 'feature', text: 'Added bundle signing and verification' },
        { type: 'feature', text: 'New audit log export functionality' },
        { type: 'improvement', text: 'Streamlined SDK initialization' },
        { type: 'deprecation', text: 'Deprecated legacy authentication flow' },
      ],
    },
    {
      version: '1.0.0',
      date: '2023-12-01',
      type: 'major',
      changes: [
        { type: 'feature', text: 'Initial stable release' },
        { type: 'feature', text: 'Core policy evaluation engine' },
        { type: 'feature', text: 'TypeScript SDK with full type support' },
        { type: 'feature', text: 'CLI for policy management' },
        { type: 'feature', text: 'REST API with OpenAPI specification' },
        { type: 'feature', text: 'Bundle compilation and distribution' },
      ],
    },
  ];

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'feature':
        return 'bg-green-100 text-green-800';
      case 'improvement':
        return 'bg-blue-100 text-blue-800';
      case 'fix':
        return 'bg-amber-100 text-amber-800';
      case 'deprecation':
        return 'bg-red-100 text-red-800';
      case 'docs':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getVersionBadge = (type: string) => {
    switch (type) {
      case 'major':
        return 'bg-red-500';
      case 'minor':
        return 'bg-blue-500';
      case 'patch':
        return 'bg-slate-500';
      default:
        return 'bg-slate-500';
    }
  };

  return (
    <article className="prose prose-slate max-w-none">
      <h1>Changelog</h1>
      <p className="lead">Track all changes, improvements, and fixes to the Opair platform.</p>

      <div className="not-prose space-y-8 mt-8">
        {releases.map((release) => (
          <div key={release.version} className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <span className="text-xl font-bold text-slate-900">v{release.version}</span>
                <span
                  className={`px-2 py-0.5 text-xs font-medium text-white rounded ${getVersionBadge(release.type)}`}
                >
                  {release.type}
                </span>
              </div>
              <time className="text-sm text-slate-500">{release.date}</time>
            </div>
            <ul className="divide-y divide-slate-100">
              {release.changes.map((change, index) => (
                <li key={index} className="flex items-start gap-3 px-6 py-3">
                  <span
                    className={`px-2 py-0.5 text-xs font-medium rounded ${getTypeColor(change.type)}`}
                  >
                    {change.type}
                  </span>
                  <span className="text-sm text-slate-700">{change.text}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="not-prose mt-12 p-6 bg-slate-100 rounded-lg text-center">
        <h3 className="text-lg font-semibold text-slate-900">Stay Updated</h3>
        <p className="text-slate-600 mt-2">
          Subscribe to release notifications to stay informed about new features and updates.
        </p>
        <a
          href="https://github.com/opair-platform/opair/releases"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block mt-4 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors"
        >
          View on GitHub
        </a>
      </div>
    </article>
  );
}
